/**
 * Integration hooks for React Query
 *
 * Provides data fetching and mutation hooks for integration management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as integrationsApi from '@/api/integrations';
import type {
  AsanaConfig,
  AsanaProjectMapping,
  GoogleCalendarConfig,
  GitHubJournalConfig,
  SpreadsheetImportConfig,
} from '@/api/integrations';

// Query keys
export const integrationKeys = {
  all: ['integrations'] as const,
  asana: () => [...integrationKeys.all, 'asana'] as const,
  asanaConfig: () => [...integrationKeys.asana(), 'config'] as const,
  asanaStatus: () => [...integrationKeys.asana(), 'status'] as const,
  asanaWorkspaces: () => [...integrationKeys.asana(), 'workspaces'] as const,
  asanaProjects: (workspaceGid: string) =>
    [...integrationKeys.asana(), 'projects', workspaceGid] as const,
  googleCalendar: () => [...integrationKeys.all, 'google-calendar'] as const,
  googleCalendarConfig: () => [...integrationKeys.googleCalendar(), 'config'] as const,
  googleCalendarStatus: () => [...integrationKeys.googleCalendar(), 'status'] as const,
  googleCalendarCalendars: () => [...integrationKeys.googleCalendar(), 'calendars'] as const,
  githubJournal: () => [...integrationKeys.all, 'github-journal'] as const,
  githubJournalConfig: () => [...integrationKeys.githubJournal(), 'config'] as const,
  githubJournalStatus: () => [...integrationKeys.githubJournal(), 'status'] as const,
  spreadsheetImport: () => [...integrationKeys.all, 'spreadsheet-import'] as const,
  spreadsheetConfigs: () => [...integrationKeys.spreadsheetImport(), 'configs'] as const,
  spreadsheetConfig: (configId: string) =>
    [...integrationKeys.spreadsheetImport(), 'config', configId] as const,
  spreadsheetStatus: (configId?: string) =>
    [...integrationKeys.spreadsheetImport(), 'status', configId] as const,
};

/**
 * Hook for getting Asana configuration
 */
export function useAsanaConfig() {
  return useQuery({
    queryKey: integrationKeys.asanaConfig(),
    queryFn: () => integrationsApi.getAsanaConfig(),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for getting Asana sync status
 */
export function useAsanaSyncStatus() {
  return useQuery({
    queryKey: integrationKeys.asanaStatus(),
    queryFn: () => integrationsApi.getAsanaSyncStatus(),
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 1,
  });
}

/**
 * Hook for getting Asana workspaces
 */
export function useAsanaWorkspaces(enabled = true) {
  return useQuery({
    queryKey: integrationKeys.asanaWorkspaces(),
    queryFn: () => integrationsApi.getAsanaWorkspaces(),
    enabled,
    retry: 1,
  });
}

/**
 * Hook for getting Asana projects in a workspace
 */
export function useAsanaProjects(workspaceGid: string | null, enabled = true) {
  return useQuery({
    queryKey: integrationKeys.asanaProjects(workspaceGid || ''),
    queryFn: () => integrationsApi.getAsanaProjects(workspaceGid!),
    enabled: enabled && Boolean(workspaceGid),
    retry: 1,
  });
}

/**
 * Hook for Asana configuration mutations
 */
export function useAsanaConfigMutations() {
  const queryClient = useQueryClient();

  const invalidateAsana = () => {
    queryClient.invalidateQueries({ queryKey: integrationKeys.asana() });
  };

  // Update config
  const updateConfig = useMutation({
    mutationFn: (config: Partial<AsanaConfig>) => integrationsApi.updateAsanaConfig(config),
    onSuccess: () => {
      invalidateAsana();
    },
  });

  // Test connection
  const testConnection = useMutation({
    mutationFn: () => integrationsApi.testAsanaConnection(),
  });

  // Trigger sync
  const triggerSync = useMutation({
    mutationFn: (direction: 'both' | 'inbound' | 'outbound' = 'both') =>
      integrationsApi.triggerAsanaSync(direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.asanaStatus() });
    },
  });

  // Save project mapping
  const saveMapping = useMutation({
    mutationFn: ({ project, mapping }: { project: string; mapping: AsanaProjectMapping }) =>
      integrationsApi.saveProjectMapping(project, mapping),
    onSuccess: () => {
      invalidateAsana();
    },
  });

  // Delete project mapping
  const deleteMapping = useMutation({
    mutationFn: (project: string) => integrationsApi.deleteProjectMapping(project),
    onSuccess: () => {
      invalidateAsana();
    },
  });

  return {
    updateConfig,
    testConnection,
    triggerSync,
    saveMapping,
    deleteMapping,
  };
}

// ========================================
// Google Calendar Hooks
// ========================================

/**
 * Hook for getting Google Calendar configuration
 */
export function useGoogleCalendarConfig() {
  return useQuery({
    queryKey: integrationKeys.googleCalendarConfig(),
    queryFn: () => integrationsApi.getGoogleCalendarConfig(),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for getting Google Calendar sync status
 */
export function useGoogleCalendarSyncStatus() {
  return useQuery({
    queryKey: integrationKeys.googleCalendarStatus(),
    queryFn: () => integrationsApi.getGoogleCalendarSyncStatus(),
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 1,
  });
}

/**
 * Hook for getting available Google Calendars
 */
export function useGoogleCalendars(enabled = true) {
  return useQuery({
    queryKey: integrationKeys.googleCalendarCalendars(),
    queryFn: () => integrationsApi.getGoogleCalendars(),
    enabled,
    retry: 1,
  });
}

/**
 * Hook for Google Calendar configuration mutations
 */
export function useGoogleCalendarMutations() {
  const queryClient = useQueryClient();

  const invalidateGoogleCalendar = () => {
    queryClient.invalidateQueries({ queryKey: integrationKeys.googleCalendar() });
  };

  // Get auth URL
  const getAuthUrl = useMutation({
    mutationFn: () => integrationsApi.getGoogleCalendarAuthUrl(),
  });

  // Handle OAuth callback
  const handleCallback = useMutation({
    mutationFn: ({ code, state }: { code: string; state?: string }) =>
      integrationsApi.handleGoogleCalendarCallback(code, state),
    onSuccess: () => {
      invalidateGoogleCalendar();
    },
  });

  // Update config
  const updateConfig = useMutation({
    mutationFn: (config: Partial<GoogleCalendarConfig>) =>
      integrationsApi.updateGoogleCalendarConfig(config),
    onSuccess: () => {
      invalidateGoogleCalendar();
    },
  });

  // Test connection
  const testConnection = useMutation({
    mutationFn: () => integrationsApi.testGoogleCalendarConnection(),
  });

  // Trigger sync
  const triggerSync = useMutation({
    mutationFn: (direction: 'both' | 'inbound' | 'outbound' = 'both') =>
      integrationsApi.triggerGoogleCalendarSync(direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.googleCalendarStatus() });
    },
  });

  // Disconnect
  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnectGoogleCalendar(),
    onSuccess: () => {
      invalidateGoogleCalendar();
    },
  });

  return {
    getAuthUrl,
    handleCallback,
    updateConfig,
    testConnection,
    triggerSync,
    disconnect,
  };
}

// ========================================
// GitHub Journal Hooks (LogSeq/Obsidian)
// ========================================

/**
 * Hook for getting GitHub Journal configuration
 */
export function useGitHubJournalConfig() {
  return useQuery({
    queryKey: integrationKeys.githubJournalConfig(),
    queryFn: () => integrationsApi.getGitHubJournalConfig(),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for getting GitHub Journal sync status
 */
export function useGitHubJournalSyncStatus() {
  return useQuery({
    queryKey: integrationKeys.githubJournalStatus(),
    queryFn: () => integrationsApi.getGitHubJournalSyncStatus(),
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 1,
  });
}

/**
 * Hook for GitHub Journal configuration mutations
 */
export function useGitHubJournalMutations() {
  const queryClient = useQueryClient();

  const invalidateGitHubJournal = () => {
    queryClient.invalidateQueries({ queryKey: integrationKeys.githubJournal() });
  };

  // Update config (includes initial setup with token)
  const updateConfig = useMutation({
    mutationFn: (config: Partial<GitHubJournalConfig> & { githubToken?: string }) =>
      integrationsApi.updateGitHubJournalConfig(config),
    onSuccess: () => {
      invalidateGitHubJournal();
    },
  });

  // Test connection
  const testConnection = useMutation({
    mutationFn: () => integrationsApi.testGitHubJournalConnection(),
  });

  // Trigger sync
  const triggerSync = useMutation({
    mutationFn: (direction: 'both' | 'inbound' | 'outbound' = 'both') =>
      integrationsApi.triggerGitHubJournalSync(direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.githubJournalStatus() });
    },
  });

  // Disconnect
  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnectGitHubJournal(),
    onSuccess: () => {
      invalidateGitHubJournal();
    },
  });

  return {
    updateConfig,
    testConnection,
    triggerSync,
    disconnect,
  };
}

// ========================================
// Spreadsheet Import Hooks (Jira)
// ========================================

/**
 * Hook for getting all spreadsheet import configurations
 */
export function useSpreadsheetConfigs() {
  return useQuery({
    queryKey: integrationKeys.spreadsheetConfigs(),
    queryFn: () => integrationsApi.getSpreadsheetConfigs(),
    retry: 1,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for getting a specific spreadsheet import configuration
 */
export function useSpreadsheetConfig(configId: string) {
  return useQuery({
    queryKey: integrationKeys.spreadsheetConfig(configId),
    queryFn: () => integrationsApi.getSpreadsheetConfig(configId),
    enabled: Boolean(configId),
    retry: 1,
  });
}

/**
 * Hook for getting spreadsheet import status
 */
export function useSpreadsheetStatus(configId?: string) {
  return useQuery({
    queryKey: integrationKeys.spreadsheetStatus(configId),
    queryFn: () => integrationsApi.getSpreadsheetStatus(configId),
    refetchInterval: 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook for spreadsheet import mutations
 */
export function useSpreadsheetImportMutations() {
  const queryClient = useQueryClient();

  const invalidateSpreadsheet = () => {
    queryClient.invalidateQueries({ queryKey: integrationKeys.spreadsheetImport() });
  };

  // Create new configuration
  const createConfig = useMutation({
    mutationFn: (config: Parameters<typeof integrationsApi.createSpreadsheetConfig>[0]) =>
      integrationsApi.createSpreadsheetConfig(config),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Update configuration
  const updateConfig = useMutation({
    mutationFn: ({
      configId,
      ...config
    }: { configId: string } & Partial<SpreadsheetImportConfig>) =>
      integrationsApi.updateSpreadsheetConfig(configId, config),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Delete configuration
  const deleteConfig = useMutation({
    mutationFn: (configId: string) => integrationsApi.deleteSpreadsheetConfig(configId),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Upload and import file
  const uploadFile = useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File;
      options?: { configId?: string; defaultProjectTopic?: string };
    }) => integrationsApi.uploadSpreadsheet(file, options),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Preview import
  const previewImport = useMutation({
    mutationFn: ({ file, configId }: { file: File; configId?: string }) =>
      integrationsApi.previewSpreadsheetImport(file, configId),
  });

  // Detect file format
  const detectFormat = useMutation({
    mutationFn: (file: File) => integrationsApi.detectSpreadsheetFormat(file),
  });

  // Link Google Drive file
  const linkGoogleDrive = useMutation({
    mutationFn: (options: Parameters<typeof integrationsApi.linkGoogleDriveSpreadsheet>[0]) =>
      integrationsApi.linkGoogleDriveSpreadsheet(options),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Link OneDrive file
  const linkOnedrive = useMutation({
    mutationFn: (options: Parameters<typeof integrationsApi.linkOnedriveSpreadsheet>[0]) =>
      integrationsApi.linkOnedriveSpreadsheet(options),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  // Trigger sync
  const triggerSync = useMutation({
    mutationFn: (configId: string) => integrationsApi.triggerSpreadsheetSync(configId),
    onSuccess: () => {
      invalidateSpreadsheet();
    },
  });

  return {
    createConfig,
    updateConfig,
    deleteConfig,
    uploadFile,
    previewImport,
    detectFormat,
    linkGoogleDrive,
    linkOnedrive,
    triggerSync,
  };
}

// ========================================
// GitHub Jira Sync Hooks (Two-Stage Workflow)
// ========================================

/**
 * Hook for GitHub sync status
 */
export function useGitHubSyncStatus(configId: string) {
  return useQuery({
    queryKey: ['integrations', 'github-sync', 'status', configId],
    queryFn: () => integrationsApi.getGitHubSyncStatus(configId),
    enabled: Boolean(configId),
    refetchInterval: 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook for GitHub conflict report
 */
export function useGitHubConflictReport(configId: string, enabled = false) {
  return useQuery({
    queryKey: ['integrations', 'github-sync', 'conflict-report', configId],
    queryFn: () => integrationsApi.getGitHubConflictReport(configId),
    enabled: Boolean(configId) && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for GitHub delta report
 */
export function useGitHubDeltaReport(configId: string, enabled = false) {
  return useQuery({
    queryKey: ['integrations', 'github-sync', 'delta-report', configId],
    queryFn: () => integrationsApi.getGitHubDeltaReport(configId),
    enabled: Boolean(configId) && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for GitHub sync mutations
 */
export function useGitHubSyncMutations() {
  const queryClient = useQueryClient();

  const invalidateGitHubSync = () => {
    queryClient.invalidateQueries({ queryKey: ['integrations', 'github-sync'] });
    queryClient.invalidateQueries({ queryKey: integrationKeys.spreadsheetImport() });
  };

  // Link GitHub repository
  const linkGitHub = useMutation({
    mutationFn: (options: Parameters<typeof integrationsApi.linkGitHubRepository>[0]) =>
      integrationsApi.linkGitHubRepository(options),
    onSuccess: () => {
      invalidateGitHubSync();
    },
  });

  // Test GitHub connection
  const testConnection = useMutation({
    mutationFn: (options: Parameters<typeof integrationsApi.testGitHubConnection>[0]) =>
      integrationsApi.testGitHubConnection(options),
  });

  // Trigger sync
  const triggerSync = useMutation({
    mutationFn: (configId: string) => integrationsApi.triggerGitHubSync(configId),
    onSuccess: () => {
      invalidateGitHubSync();
    },
  });

  // Prepare JIRA import file
  const prepareJiraImport = useMutation({
    mutationFn: (configId: string) => integrationsApi.prepareJiraImportFile(configId),
  });

  return {
    linkGitHub,
    testConnection,
    triggerSync,
    prepareJiraImport,
  };
}

